import React, { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { initializeAnalytics, trackPageView, trackSessionStart } from './utils/analytics';
import { initWebVitals, trackPageLoad } from './utils/webVitals';
import Header from './components/Header';
import FeedbackTrigger from './components/FeedbackTrigger';
import LandingPage from './pages/LandingPage';
import UserDashboard from './components/UserDashboard';
import GrammarPage from './components/GrammarPage';
import SentencePracticePage from './components/SentencePracticePage';
import DictionaryPage from './components/DictionaryPage';
import MemorizeGrammarPage from './components/MemorizeGrammarPage';
import MemorizeSentencesPage from './components/MemorizeSentencesPage';
import MemorizeWordsPage from './components/MemorizeWordsPage';
import FavoritesGrammarPage from './components/FavoritesGrammarPage';
import FavoriteSentencesCard from './components/FavoriteSentencesCard';
import FavoriteWordsCard from './components/FavoriteWordsCard';
import CompletedGrammarPage from './components/CompletedGrammarPage';
import CompletedSentencesCard from './components/CompletedSentencesCard';
import CompletedWordsCard from './components/CompletedWordsCard';
import Login from './components/Login';
import Signup from './components/Signup';
import HomePage from './pages/HomePage';
import { UserDataProvider } from './contexts/UserDataContext';
import { LanguageToggleProvider } from './contexts/LanguageToggleContext';
import AboutUs from './pages/AboutUs';
import HelpSupport from './pages/HelpSupport';
import PrivacyTerms from './pages/PrivacyTerms';
import CommunityGuidelines from './pages/CommunityGuidelines';
import ConversationsPage from './pages/ConversationsPage';
import ForgotPassword from './pages/ForgotPassword';
import RoboTeacherPage from './pages/RoboTeacherPage';
import DeleteAccount from './pages/DeleteAccount';
import ApiTest from './components/ApiTest';
import BlogList from './components/blog/BlogList';
import BlogPost from './components/blog/BlogPost';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize Google Analytics on app start
  useEffect(() => {
    initializeAnalytics();
    trackSessionStart();

    // Initialize Core Web Vitals tracking
    initWebVitals();
    trackPageLoad();
  }, []);

  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Only show header on /home and app pages, not on /, /login, or /signup
  const showHeader = !['/', '/login', '/signup'].includes(location.pathname);

  return (
    <HelmetProvider>
      <LanguageToggleProvider>
        <UserDataProvider>
          {showHeader && <Header />}
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
            <Route path="/login" element={<PublicRoute><Login onSwitchToSignup={() => navigate('/signup')} onClose={() => navigate('/')} /></PublicRoute>} />
            <Route path="/signup" element={<PublicRoute><Signup onSwitchToLogin={() => navigate('/login')} onClose={() => navigate('/')} /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/grammar" element={<ProtectedRoute><GrammarPage /></ProtectedRoute>} />
            <Route path="/sentences" element={<ProtectedRoute><SentencePracticePage /></ProtectedRoute>} />
            <Route path="/dictionary" element={<ProtectedRoute><DictionaryPage /></ProtectedRoute>} />
            <Route path="/conversations" element={<ProtectedRoute><ConversationsPage /></ProtectedRoute>} />
            <Route path="/robo-teacher" element={<ProtectedRoute><RoboTeacherPage /></ProtectedRoute>} />
            <Route path="/memorize-grammar" element={<ProtectedRoute><MemorizeGrammarPage userId="" /></ProtectedRoute>} />
            <Route path="/memorize-sentences" element={<ProtectedRoute><MemorizeSentencesPage userId="" /></ProtectedRoute>} />
            <Route path="/memorize-words" element={<ProtectedRoute><MemorizeWordsPage userId="" /></ProtectedRoute>} />
            <Route path="/favorite-grammar" element={<ProtectedRoute><FavoritesGrammarPage /></ProtectedRoute>} />
            <Route path="/favorite-sentences" element={<ProtectedRoute><FavoriteSentencesCard /></ProtectedRoute>} />
            <Route path="/favorite-words" element={<ProtectedRoute><FavoriteWordsCard /></ProtectedRoute>} />
            <Route path="/completed-grammar" element={<ProtectedRoute><CompletedGrammarPage /></ProtectedRoute>} />
            <Route path="/completed-sentences" element={<ProtectedRoute><CompletedSentencesCard /></ProtectedRoute>} />
            <Route path="/completed-words" element={<ProtectedRoute><CompletedWordsCard /></ProtectedRoute>} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/help" element={<HelpSupport />} />
            <Route path="/privacy" element={<PrivacyTerms />} />
            <Route path="/community" element={<CommunityGuidelines />} />
            <Route path="/search" element={<div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center"><div className="text-center"><h1 className="text-3xl font-bold text-gray-800 mb-4">Search Feature</h1><p className="text-gray-600">Search functionality coming soon!</p></div></div>} />
            <Route path="/api-test" element={<ProtectedRoute><ApiTest /></ProtectedRoute>} />
                        <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* Catch-all route for 404s */}
            <Route path="*" element={
              <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-800 mb-4">Page Not Found</h1>
                  <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
                  <button 
                    onClick={() => navigate('/home')} 
                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full hover:scale-105 transition-all duration-200"
                  >
                    Go to Home
                  </button>
                </div>
              </div>
            } />
        </Routes>

          {/* Feedback Trigger - appears after login */}
          <FeedbackTrigger />
        </UserDataProvider>
      </LanguageToggleProvider>
    </HelmetProvider>
  );
}

export default App;
